import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jobsAPI, getErrorMessage } from '../services/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import type { Job, CreateJobRequest } from '../types';

function JobsPage() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [formData, setFormData] = useState<CreateJobRequest>({
    title: '',
    company: '',
    description: '',
  });

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const data = await jobsAPI.getAll();
      setJobs(data);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err) || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingJob(null);
    setFormData({ title: '', company: '', description: '' });
    setShowModal(true);
  };

  const handleEdit = (job: Job) => {
    setEditingJob(job);
    setFormData({
      title: job.title,
      company: job.company,
      description: job.description || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }

    try {
      await jobsAPI.delete(id);
      loadJobs();
    } catch (err) {
      alert(getErrorMessage(err) || 'Failed to delete job');
    }
  };

  const handleClose = async (id: number) => {
    if (!window.confirm('Are you sure you want to close this job?')) {
      return;
    }

    try {
      await jobsAPI.close(id);
      loadJobs();
    } catch (err) {
      alert(getErrorMessage(err) || 'Failed to close job');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingJob) {
        await jobsAPI.update(editingJob.id, formData);
      } else {
        await jobsAPI.create(formData);
      }
      setShowModal(false);
      loadJobs();
    } catch (err) {
      alert(getErrorMessage(err) || 'Failed to save job');
    }
  };

  if (loading) {
    return (
      <div>
        <div className="mb-8">
          <h2 className="text-3xl font-semibold text-dark mb-2">Jobs</h2>
          <p className="text-sm text-gray-600">Manage job postings and recruitment tasks</p>
        </div>
        <div className="text-center py-10 text-gray-600">Loading jobs...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-semibold text-dark mb-2">Jobs</h2>
        <p className="text-sm text-gray-600">Manage job postings and recruitment tasks</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-danger-light text-danger-text px-4 py-3 rounded-md mb-5">
          {error}
        </div>
      )}

      {/* Stats and Create Button */}
      <div className="flex justify-between items-center mb-5">
        <div className="grid grid-cols-2 gap-5 flex-1">
          <Card className="p-5">
            <h4 className="text-xs uppercase tracking-wider text-gray-600 mb-2">Total Jobs</h4>
            <p className="text-3xl font-bold text-dark">{jobs.length}</p>
          </Card>
          <Card className="p-5">
            <h4 className="text-xs uppercase tracking-wider text-gray-600 mb-2">Active Jobs</h4>
            <p className="text-3xl font-bold text-dark">{jobs.filter(j => j.status === 'active').length}</p>
          </Card>
        </div>
        <Button onClick={handleCreate} className="ml-5">
          + Create Job
        </Button>
      </div>

      {/* Jobs List or Empty State */}
      {jobs.length === 0 ? (
        <div className="text-center py-16">
          <h3 className="text-lg font-medium text-gray-700 mb-2">No jobs found</h3>
          <p className="text-gray-500 mb-5">Create your first job posting to get started</p>
          <Button onClick={handleCreate}>
            + Create Job
          </Button>
        </div>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">ID</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Company</TableHead>
                <TableHead className="w-24">Status</TableHead>
                <TableHead className="w-32">Created At</TableHead>
                <TableHead className="w-80">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map(job => (
                <TableRow key={job.id}>
                  <TableCell>{job.id}</TableCell>
                  <TableCell>
                    <button
                      onClick={() => navigate(`/dashboard/jobs/${job.id}`)}
                      className="font-semibold text-dark hover:text-primary hover:underline cursor-pointer bg-transparent border-none p-0 text-left"
                    >
                      {job.title}
                    </button>
                  </TableCell>
                  <TableCell>{job.company}</TableCell>
                  <TableCell>
                    <Badge variant={job.status === 'active' ? 'success' : 'warning'}>
                      {job.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{new Date(job.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => navigate(`/dashboard/jobs/${job.id}`)}>
                        View Detail
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => handleEdit(job)}>
                        Edit
                      </Button>
                      {job.status === 'active' && (
                        <Button size="sm" variant="warning" onClick={() => handleClose(job.id)}>
                          Close
                        </Button>
                      )}
                      <Button size="sm" variant="danger" onClick={() => handleDelete(job.id)}>
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>{editingJob ? 'Edit Job' : 'Create New Job'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="title">Job Title *</Label>
                <Input
                  id="title"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Senior Software Engineer"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company">Company *</Label>
                <Input
                  id="company"
                  required
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="e.g., Acme Inc."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Job description, requirements, responsibilities..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingJob ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default JobsPage;
